import { Injectable, inject } from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {TenantService} from './tenant.service';
import {LoadingService} from '../shared/services/loading.service';
import {from, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import { Auth, fetchSignInMethodsForEmail } from '@angular/fire/auth';

/**
 * Resolves which sign-in methods the current student has ('password', 'google.com', ...), so
 * My Account knows whether to offer "change password" and similar.
 *
 * A resolver that errors makes Angular CANCEL the navigation. This one used to call
 * fetchSignInMethodsForEmail(auth, currentUser.email) with no guard, so any session without an
 * email made Firebase throw auth/missing-identifier - and clicking My Account simply did nothing,
 * with only a console error to show for it. Every other page in that menu worked, because only
 * this route carries the resolver.
 *
 * Sessions with no email are real: signing in with a custom token (as "Login as student" does)
 * for a uid that no longer has a Firebase Auth record makes Firebase create a bare record - no
 * email, no providers. So this now:
 *   - never blocks the navigation: any failure resolves to what we already know locally;
 *   - skips the network call entirely when there is no email to ask about;
 *   - falls back to the providers already linked to the session when Firebase returns nothing,
 *     which it does for every address once email enumeration protection is switched on.
 */
@Injectable({
  providedIn:'root'
})
export class SignInMethodsResolver  {
  private tenant = inject(TenantService);
  private loadingService = inject(LoadingService);
  private afAuth = inject(Auth);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<string[]> {

    const user = this.afAuth.currentUser;

    // What this session is already linked to - read locally, needs no email and no request.
    const linkedProviders: string[] = (user?.providerData ?? [])
      .map((provider) => provider?.providerId)
      .filter((providerId): providerId is string => !!providerId);

    if (!user?.email) {
      return of(linkedProviders);
    }

    const signInMethods$ = from(fetchSignInMethodsForEmail(this.afAuth, user.email)).pipe(
      map((methods) => (methods?.length ? methods : linkedProviders)),
      catchError((error) => {
        console.warn('Could not load sign-in methods, falling back to the linked providers.', error);
        return of(linkedProviders);
      })
    );

    return this.loadingService.showLoaderUntilCompleted(signInMethods$);
  }

}
